import {
  Box,
  type PaperProps,
  Table,
  useMediaQuery,
  useTheme,
  TableContainer,
  type TableContainerProps,
  type TableProps,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import React from "react";

import { AdminSectionPanel } from "./AdminSectionPanel";
import { unifiedTableSx } from "./unifiedTableSx";

interface AdminTablePanelProps {
  children: React.ReactNode;
  /** Page-owned compact records rendered below md, within this panel. */
  mobileContent?: React.ReactNode;
  /** Optional, fully composable content above the table, such as a title, labels, or controls. */
  header?: React.ReactNode;
  /** Optional content below the table, such as pagination controls. */
  footer?: React.ReactNode;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  containerProps?: Omit<TableContainerProps, "children" | "component">;
  elevation?: PaperProps["elevation"];
  headerSx?: SxProps<Theme>;
  paperProps?: Omit<PaperProps, "children" | "elevation" | "sx">;
  paperSx?: SxProps<Theme>;
  tableProps?: Omit<TableProps, "children" | "sx">;
  tableSx?: SxProps<Theme>;
}

/**
 * Shared table panel for tables that belong within a larger paper section.
 * The header intentionally accepts arbitrary content so pages can compose titles,
 * summaries, filters, and switches without introducing a page-specific frame.
 */
export const AdminTablePanel: React.FC<AdminTablePanelProps> = ({
  children,
  mobileContent,
  header,
  footer,
  title,
  icon,
  actions,
  containerProps,
  elevation = 1,
  headerSx,
  paperProps,
  paperSx,
  tableProps,
  tableSx,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { sx: containerSx, ...restContainerProps } = containerProps ?? {};
  const resolvedTableSx: SxProps<Theme> = [
    unifiedTableSx,
    {
      "& .MuiTableHead-root .MuiTableCell-root": {
        borderTop: 0,
      },
      "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
        borderInlineStart: 0,
        borderStartStartRadius: 0,
      },
      "& .MuiTableHead-root .MuiTableCell-root:last-of-type": {
        borderInlineEnd: 0,
        borderStartEndRadius: 0,
      },
    },
    ...(tableSx ? (Array.isArray(tableSx) ? tableSx : [tableSx]) : []),
  ];

  return (
    <AdminSectionPanel
      elevation={elevation}
      paperSx={paperSx}
      header={header}
      title={title}
      icon={icon}
      actions={actions}
      headerSx={headerSx}
      paperProps={paperProps}
    >
      {isMobile && mobileContent !== undefined ? (
        mobileContent
      ) : (
        <TableContainer
          sx={[...(containerSx ? (Array.isArray(containerSx) ? containerSx : [containerSx]) : [])]}
          {...restContainerProps}
        >
          <Table {...tableProps} sx={resolvedTableSx}>
            {children}
          </Table>
        </TableContainer>
      )}
      {footer && <Box sx={{ mt: 1 }}>{footer}</Box>}
    </AdminSectionPanel>
  );
};
